import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import {
  MagnifyingGlass, Sparkle, CalendarBlank, ChatCircle, User, GearSix,
  ShieldCheck, Storefront, FileText, ArrowRight, EnvelopeSimple,
} from '@phosphor-icons/react';
import { ALL_CATEGORY_OPTIONS } from '@/lib/serviceCategories';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Categories come from the shared source of truth so search results always filter correctly.
const CATEGORIES = ALL_CATEGORY_OPTIONS;

const backendUrl = (u) => (u && u.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function GlobalSearch({ variant = 'desktop', autoFocus = false, onNavigate }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const dashTo = !user ? '/auth' : user.role === 'admin' ? '/admin' : user.role === 'provider' ? '/provider/dashboard' : '/dashboard';
  const ic = 'text-muted-foreground';

  // Pages & features index (auth-aware)
  const FEATURES = [
    { label: 'AI Event Planner', sub: 'Plan your whole event with AI', to: user ? '/ai-planner' : '/auth', icon: <Sparkle size={15} weight="fill" className="text-accent-coral" />, kw: ['ai', 'planner', 'plan', 'event', 'assistant'] },
    { label: 'Event Coordinator', sub: 'Day-of coordination after you book', to: user ? '/coordinator' : '/auth', icon: <CalendarBlank size={15} className={ic} />, kw: ['coordinator', 'run of show', 'timeline', 'coordinate', 'day of', 'schedule'] },
    { label: 'Invitations', sub: 'Design & send invites, track RSVPs', to: user ? '/invitations' : '/i/demo', icon: <EnvelopeSimple size={15} className={ic} />, kw: ['invitation', 'invite', 'evite', 'rsvp', 'card', 'flyer', 'save the date', 'paperless'] },
    { label: 'Browse all services', sub: 'Find & compare providers', to: '/browse', icon: <MagnifyingGlass size={15} className={ic} />, kw: ['browse', 'services', 'providers', 'find', 'search', 'explore'] },
    ...(user
      ? [
          { label: 'Messages', sub: 'Chat with your providers', to: '/messages', icon: <ChatCircle size={15} className={ic} />, kw: ['messages', 'chat', 'inbox'] },
          { label: 'My Dashboard', sub: 'Bookings & activity', to: dashTo, icon: <User size={15} className={ic} />, kw: ['dashboard', 'bookings', 'account', 'home'] },
          { label: 'Settings', sub: 'Profile & preferences', to: '/profile', icon: <GearSix size={15} className={ic} />, kw: ['settings', 'profile', 'preferences', 'password'] },
        ]
      : [
          { label: 'Sign in', sub: 'Access your account', to: '/auth', icon: <User size={15} className={ic} />, kw: ['sign in', 'login', 'account'] },
        ]),
    { label: 'Become a provider', sub: 'List your services on Bookr', to: user && user.role === 'customer' ? '/provider/setup-profile' : '/auth?mode=register&role=provider', icon: <Storefront size={15} className={ic} />, kw: ['provider', 'vendor', 'apply', 'sell', 'offer services', 'become', 'list'] },
    { label: 'Trust & Safety', sub: 'The Bookr Guarantee & protection', to: '/trust', icon: <ShieldCheck size={15} className={ic} />, kw: ['trust', 'safety', 'guarantee', 'escrow', 'protection', 'refund', 'secure'] },
    { label: 'Cancellation policy', sub: 'Refunds & cancellations', to: '/legal/cancellation', icon: <FileText size={15} className={ic} />, kw: ['cancellation', 'refund', 'policy', 'cancel'] },
    { label: 'Terms of Service', sub: '', to: '/legal/terms', icon: <FileText size={15} className={ic} />, kw: ['terms', 'legal', 'service'] },
    { label: 'Privacy Policy', sub: '', to: '/legal/privacy', icon: <FileText size={15} className={ic} />, kw: ['privacy', 'data', 'legal'] },
  ];

  const q = query.trim().toLowerCase();

  // Debounced provider lookup
  useEffect(() => {
    if (q.length < 2) { setProviders([]); setLoading(false); return; }
    setLoading(true);
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/providers?search=${encodeURIComponent(q)}&limit=6`, { signal: ctrl.signal });
        setProviders(r.data.providers || []);
      } catch (e) { if (e.name !== 'CanceledError' && e.name !== 'AbortError') setProviders([]); }
      finally { setLoading(false); }
    }, 220);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => { setHighlight(-1); }, [query]);

  // Build grouped results
  const featureMatches = q
    ? FEATURES.filter(f => f.label.toLowerCase().includes(q) || f.kw.some(k => k.includes(q) || q.includes(k)))
    : FEATURES.slice(0, 4); // curated set when empty & focused
  const catMatches = q ? CATEGORIES.filter(c => c.name.toLowerCase().includes(q)) : [];

  const groups = [];
  if (featureMatches.length) groups.push({ title: q ? 'Pages & features' : 'Jump to', items: featureMatches.map(f => ({ ...f, type: 'feature' })) });
  if (catMatches.length) groups.push({ title: 'Service categories', items: catMatches.map(c => ({ label: c.name, sub: `Browse ${c.name}`, to: `/browse?category=${encodeURIComponent(c.name)}`, emoji: c.emoji, type: 'category' })) });
  if (providers.length) groups.push({ title: 'Providers', items: providers.map(p => ({ label: p.business_name || 'Provider', sub: [p.category, p.city].filter(Boolean).join(' · '), to: `/provider/${p.profile_id}`, avatar: p.user?.avatar_url, type: 'provider' })) });

  // Flatten with global indices for keyboard nav
  let gi = 0;
  groups.forEach(g => g.items.forEach(it => { it._i = gi++; }));
  const flat = groups.flatMap(g => g.items);

  const go = (to) => {
    setOpen(false);
    setQuery('');
    onNavigate?.();
    navigate(to);
  };

  const submitSearch = () => {
    const term = query.trim();
    if (!term) return;
    go(`/browse?search=${encodeURIComponent(term)}`);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlight(h => Math.min(h + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight >= 0 && flat[highlight]) go(flat[highlight].to);
      else submitSearch();
    } else if (e.key === 'Escape') { setOpen(false); }
  };

  const inputCls = variant === 'mobile'
    ? 'w-full bg-secondary/60 pl-10 pr-4 py-3 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-primary/40 focus:bg-card'
    : 'w-full bg-secondary/60 border border-transparent pl-10 pr-4 py-2 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-primary/40 focus:bg-card focus:border-border placeholder:text-muted-foreground/70 transition-all duration-200';

  return (
    <div ref={boxRef} className="relative w-full" data-testid="global-search">
      <MagnifyingGlass size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search anything — services, providers, AI Planner…"
        aria-label="Search Bookr.ai"
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        className={inputCls}
        autoFocus={autoFocus}
        data-testid="global-search-input"
      />

      {open && (query || featureMatches.length > 0) && (
        <div
          className="absolute left-0 right-0 mt-2 bg-card border border-border/70 rounded-2xl shadow-lift py-2 z-50 max-h-[70vh] overflow-y-auto fade-up"
          data-testid="global-search-results"
        >
          {flat.length === 0 && !loading && (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              No matches. Press Enter to search providers for “{query}”.
            </div>
          )}

          {groups.map((g) => (
            <div key={g.title} className="py-1">
              <p className="px-4 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{g.title}</p>
              {g.items.map((it) => (
                <button
                  key={`${it.type}-${it.label}-${it._i}`}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); go(it.to); }}
                  onMouseEnter={() => setHighlight(it._i)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors cursor-pointer ${highlight === it._i ? 'bg-secondary' : 'hover:bg-secondary/60'}`}
                  data-testid={`gs-item-${it.type}`}
                >
                  <span className="w-7 h-7 rounded-lg bg-secondary/70 flex items-center justify-center shrink-0 overflow-hidden">
                    {it.type === 'provider' && it.avatar
                      ? <img src={backendUrl(it.avatar)} alt="" className="w-full h-full object-cover" />
                      : it.emoji
                        ? <span className="text-sm">{it.emoji}</span>
                        : it.icon}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-foreground truncate">{it.label}</span>
                    {it.sub && <span className="block text-[11px] text-muted-foreground truncate">{it.sub}</span>}
                  </span>
                  <ArrowRight size={13} className="text-muted-foreground/50 shrink-0" />
                </button>
              ))}
            </div>
          ))}

          {query.trim() && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); submitSearch(); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 mt-1 border-t border-border/60 text-left hover:bg-secondary/60 transition-colors cursor-pointer"
              data-testid="gs-search-all"
            >
              <span className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><MagnifyingGlass size={14} className="text-primary" /></span>
              <span className="text-sm text-foreground">Search all providers for <span className="font-semibold">“{query.trim()}”</span></span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
